'use client';

import type React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import type { SoundCloudTrack } from '../../types';
import { ProgressBar } from './ProgressBar';

const PanelWrapper = styled.div<{ $visible: boolean }>`
  width: 340px;
  max-height: 80vh;
  background: var(--nav-bg, rgba(10, 10, 20, 0.97));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--nav-border, rgba(0, 255, 0, 0.25));
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow:
    0 8px 40px var(--card-shadow, rgba(0, 0, 0, 0.5)),
    0 0 28px rgba(0, 255, 0, 0.08);
  pointer-events: ${({ $visible }) => ($visible ? 'all' : 'none')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) =>
    $visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.96)'};
  transition:
    opacity 0.28s ease,
    transform 0.28s ease,
    background-color 0.3s ease,
    border-color 0.3s ease;
  overflow: hidden;

  @media (max-width: 480px) {
    width: min(340px, calc(100vw - 24px));
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BrandLabel = styled.span`
  font-size: 0.65rem;
  color: #ff2d95;
  font-weight: 700;
  letter-spacing: 1px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const QueueToggleBtn = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? '#00ff00' : 'var(--card-bg, rgba(255, 255, 255, 0.05))')};
  border: 1px solid ${({ $active }) => ($active ? '#00ff00' : 'var(--card-border, rgba(255, 255, 255, 0.15))')};
  color: ${({ $active }) => ($active ? '#000000' : 'var(--text-secondary, rgba(255, 255, 255, 0.75))')};
  cursor: pointer;
  padding: 4px 10px;
  display: flex;
  align-items: center;
  gap: 5px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: ${({ $active }) => ($active ? '700' : '600')};
  font-variant-caps: all-petite-caps;
  letter-spacing: 0.5px;
  transition: all 0.2s;

  &:hover {
    background: ${({ $active }) => ($active ? '#00ff00' : 'rgba(255, 45, 149, 0.15)')};
    border-color: ${({ $active }) => ($active ? '#00ff00' : '#ff2d95')};
    color: ${({ $active }) => ($active ? '#000000' : '#ff2d95')};
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary, rgba(255, 255, 255, 0.6));
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    color: #ff2d95;
    transform: scale(1.15);
  }
`;

const TrackInfo = styled.div`
  min-width: 0;
`;

const ArtistName = styled.p`
  margin: 0 0 2px;
  font-size: 0.65rem;
  color: var(--text-muted, rgba(255, 255, 255, 0.4));
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;
`;

const TrackName = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CtrlBtn = styled.button`
  background: none;
  border: none;
  color: var(--text-primary, #fff);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    color 0.2s,
    transform 0.15s,
    background 0.2s;

  &:hover {
    color: #00ff00;
    transform: scale(1.15);
    background: rgba(0, 255, 0, 0.08);
  }
`;

const PlayPauseBtn = styled(CtrlBtn)`
  width: 44px;
  height: 44px;
  background: #00ff00;
  border: 1px solid #00ff00;
  color: #000000;
  box-shadow: 0 0 14px rgba(0, 255, 0, 0.35);

  &:hover {
    background: #ff2d95;
    border-color: #ff2d95;
    color: #ffffff;
    box-shadow: 0 0 16px rgba(255, 45, 149, 0.4);
    transform: scale(1.1);
  }
`;

const AutoPlayBadge = styled.button<{ $on: boolean }>`
  background: ${({ $on }) => ($on ? '#00ff00' : 'var(--card-bg, rgba(255, 255, 255, 0.05))')};
  border: 1px solid ${({ $on }) => ($on ? '#00ff00' : 'var(--card-border, rgba(255, 255, 255, 0.15))')};
  color: ${({ $on }) => ($on ? '#000000' : 'var(--text-secondary, rgba(255, 255, 255, 0.6))')};
  font-size: 0.68rem;
  font-weight: ${({ $on }) => ($on ? '700' : '600')};
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;
  padding: 4px 9px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #ff2d95;
    color: ${({ $on }) => ($on ? '#000000' : '#ff2d95')};
  }
`;

const SCLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff2d95;
  padding: 8px;
  border-radius: 50%;
  transition:
    color 0.2s,
    transform 0.15s;

  &:hover {
    color: #00ff00;
    transform: scale(1.15);
  }
`;

const QueueSection = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  flex-direction: column;
  max-height: 180px;
  overflow-y: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 8px;
  gap: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 0, 0.3);
    border-radius: 2px;
  }
`;

const QueueItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.12)' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.4)' : 'transparent')};
  cursor: pointer;
  font-size: 0.75rem;
  font-variant-caps: all-petite-caps;
  color: ${({ $active }) => ($active ? '#00ff00' : 'var(--text-secondary, rgba(255, 255, 255, 0.75))')};
  transition: all 0.15s;

  &:hover {
    background: rgba(0, 255, 0, 0.08);
    color: var(--text-primary, #fff);
  }
`;

const QueueTrackName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const QueueTrackMeta = styled.span`
  font-size: 0.65rem;
  color: var(--text-muted, rgba(255, 255, 255, 0.4));
`;

interface ExpandedPanelProps {
  isExpanded: boolean;
  displayTitle: string;
  progress: number;
  buffered?: number;
  isBuffering?: boolean;
  currentTimeMs?: number;
  durationMs?: number;
  isPlaying: boolean;
  isAutoPlayEnabled: boolean;
  currentTrackUrl: string | null;
  queue?: SoundCloudTrack[];
  onClose: () => void;
  onTogglePlay: () => void;
  onToggleAutoPlay: () => void;
  onSeek: (pct: number) => void;
  onPlayNext?: () => void;
  onPlayPrev?: () => void;
  onSelectTrack?: (track: SoundCloudTrack) => void;
}

/**
 * Expanded audio control panel with next-up playlist queue and track skipping.
 */
export const ExpandedPanel: React.FC<ExpandedPanelProps> = ({
  isExpanded,
  displayTitle,
  progress,
  buffered = 0,
  isBuffering = false,
  currentTimeMs = 0,
  durationMs = 0,
  isPlaying,
  isAutoPlayEnabled,
  currentTrackUrl,
  queue = [],
  onClose,
  onTogglePlay,
  onToggleAutoPlay,
  onSeek,
  onPlayNext,
  onPlayPrev,
  onSelectTrack,
}) => {
  const [showQueue, setShowQueue] = useState(false);
  const { t } = useLanguage();

  return (
    <PanelWrapper $visible={isExpanded} role="region" aria-label="SoundCloud player">
      <PanelHeader>
        <BrandLabel>♫ SC — joev noise</BrandLabel>
        <HeaderActions>
          <QueueToggleBtn
            $active={showQueue}
            onClick={() => setShowQueue((prev) => !prev)}
            aria-label="Toggle next up playlist queue"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <line x1="8" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="8" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" />
              <line x1="8" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="6" x2="3.01" y2="6" stroke="currentColor" strokeWidth="3" />
              <line x1="3" y1="12" x2="3.01" y2="12" stroke="currentColor" strokeWidth="3" />
              <line x1="3" y1="18" x2="3.01" y2="18" stroke="currentColor" strokeWidth="3" />
            </svg>
            {t('playerNextUp')}
          </QueueToggleBtn>
          <CloseBtn onClick={onClose} aria-label={t('playerCollapse')}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseBtn>
        </HeaderActions>
      </PanelHeader>

      <TrackInfo>
        <ArtistName>joev noise</ArtistName>
        <TrackName>{displayTitle}</TrackName>
      </TrackInfo>

      <ProgressBar
        progress={progress}
        buffered={buffered}
        isBuffering={isBuffering}
        currentTimeMs={currentTimeMs}
        durationMs={durationMs}
        onSeek={onSeek}
      />

      <Controls>
        <AutoPlayBadge
          $on={isAutoPlayEnabled}
          onClick={onToggleAutoPlay}
          title={isAutoPlayEnabled ? 'Auto-play on scroll: ON' : 'Auto-play on scroll: OFF'}
          aria-pressed={isAutoPlayEnabled}
        >
          {isAutoPlayEnabled ? '⟳ auto' : '⟳ off'}
        </AutoPlayBadge>

        {onPlayPrev && (
          <CtrlBtn onClick={onPlayPrev} aria-label="Previous track" title="Previous track">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="19,20 9,12 19,4" />
              <line x1="5" y1="4" x2="5" y2="20" stroke="currentColor" strokeWidth="2.5" />
            </svg>
          </CtrlBtn>
        )}

        <PlayPauseBtn onClick={onTogglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </PlayPauseBtn>

        {onPlayNext && (
          <CtrlBtn onClick={onPlayNext} aria-label="Next track" title="Next track">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,4 15,12 5,20" />
              <line x1="19" y1="4" x2="19" y2="20" stroke="currentColor" strokeWidth="2.5" />
            </svg>
          </CtrlBtn>
        )}

        <SCLink
          href={currentTrackUrl ?? 'https://soundcloud.com/joevnoise'}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open on SoundCloud"
          title="Open on SoundCloud"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1.17 12.225c-.022 0-.042.019-.047.042l-.273 2.873c-.004.041.02.046.047.046.026 0 .046-.005.051-.046l.273-2.873a.044.044 0 0 0-.051-.042zm-.848 1.258c-.024 0-.044.019-.049.043l-.15 1.583c-.004.041.02.046.046.046.027 0 .048-.005.053-.046l.15-1.583a.046.046 0 0 0-.05-.043zm1.701-1.892c-.024 0-.044.02-.047.044l-.328 3.758c-.003.041.021.046.047.046.027 0 .048-.005.051-.046l.328-3.758a.046.046 0 0 0-.051-.044zm.85-.308c-.026 0-.046.02-.049.044l-.338 4.072c-.003.041.021.046.047.046.027 0 .048-.005.051-.046l.338-4.072a.046.046 0 0 0-.049-.044zm.849-.24c-.026 0-.046.02-.048.044l-.326 4.316c-.003.041.02.046.047.046.027 0 .048-.005.051-.046l.326-4.316a.046.046 0 0 0-.05-.044zm.85-.357c-.026 0-.046.02-.048.044l-.317 4.673c-.004.042.02.047.047.047.027 0 .047-.005.051-.047l.317-4.673a.046.046 0 0 0-.05-.044zm.853-.54c-.027 0-.047.02-.048.044l-.307 5.213c-.004.042.02.047.047.047.026 0 .047-.005.05-.047l.307-5.213a.046.046 0 0 0-.049-.044zm.849-.406c-.027 0-.047.02-.048.044l-.299 5.619c-.004.042.02.047.047.047.027 0 .047-.005.051-.047l.299-5.619a.046.046 0 0 0-.05-.044zm.85-.325c-.028 0-.048.02-.049.045l-.29 5.944c-.004.042.02.047.047.047.027 0 .047-.005.051-.047l.29-5.944a.046.046 0 0 0-.049-.045zm.85-.125c-.028 0-.048.02-.049.045l-.281 6.069c-.004.042.02.047.047.047.027 0 .047-.005.051-.047l.281-6.069a.046.046 0 0 0-.049-.045zm.85-.045c-.028 0-.048.02-.049.045l-.272 6.114c-.004.042.02.047.047.047.027 0 .047-.005.051-.047l.272-6.114a.046.046 0 0 0-.049-.045zm1.537-3.238c-.035 0-.063.028-.067.062l-.21 4.296c-.053-.591-.122-1.077-.207-1.424a.07.07 0 0 0-.07-.054c-.034 0-.061.026-.064.06l-.233 6.45c-.004.042.02.047.047.047.027 0 .047-.005.051-.047l.261-3.666c.038.528.093 1.002.164 1.397.006.035.035.059.07.059.035 0 .063-.028.067-.063l.192-7.072c0-.036-.024-.045-.062-.045zm4.847 2.375c-.246 0-.486.031-.716.088-.235-.918-.847-1.688-1.678-2.128-.415-.22-.88-.344-1.373-.344-.45 0-.877.104-1.26.29-.317.153-.599.366-.833.627a.066.066 0 0 0-.022.047c0 .03.022.057.052.062l.685.202c.031.009.064-.007.076-.037.319-.773 1.076-1.314 1.96-1.314 1.075 0 1.969.79 2.126 1.826.024.156.158.272.316.272h.43c1.696 0 3.076 1.38 3.076 3.076s-1.38 3.076-3.076 3.076h-4.636c-.039 0-.07.032-.07.071v.784c0 .039.031.071.07.071h4.636c2.204 0 3.996-1.792 3.996-3.996 0-2.186-1.761-3.967-3.94-3.996z" />
          </svg>
        </SCLink>
      </Controls>

      <QueueSection $visible={showQueue} role="list" aria-label="Upcoming tracks playlist">
        {queue.map((track) => {
          const isActive = track.soundcloudUrl === currentTrackUrl;
          return (
            <QueueItem
              key={track.id}
              $active={isActive}
              onClick={() => onSelectTrack?.(track)}
              role="listitem"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onSelectTrack?.(track);
              }}
            >
              <QueueTrackName>{track.title}</QueueTrackName>
              <QueueTrackMeta>{track.series}</QueueTrackMeta>
            </QueueItem>
          );
        })}
      </QueueSection>
    </PanelWrapper>
  );
};

export default ExpandedPanel;
